'use client';

import { Fragment } from 'react';
import type { ProductGiftTier } from '@eliadmin/core/src/api/products/types/product-types';
import { getGiftItemDisplay } from '@eliadmin/core/src/utils/cart-gift-utils';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { giftPackageLabel } from '@eliadmin/core/src/i18n/gift-labels';
import { GiftCouponValue } from '@eliadmin/core/src/components/product/gift-coupon-value';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export type GiftReward = Pick<ProductGiftTier, 'name' | 'items' | 'reward_coupon'>;

export function getGiftRewardText(reward: GiftReward, fallback: string) {
  return (
    [
      reward.reward_coupon?.name,
      ...reward.items.map(item => {
        const display = getGiftItemDisplay(item);
        return `${Number(display.quantity) === 1 ? '' : `${display.quantity} × `}${display.name || fallback}`;
      }),
    ]
      .filter(Boolean)
      .join(' + ') || fallback
  );
}

/** Names describe the next package, not a selectable tier or total order quantity. */
export function GiftRewardNames({ reward, fallback }: { reward: GiftReward; fallback: string }) {
  return (
    <>
      {reward.reward_coupon?.name}
      {reward.items.map((item, index) => {
        const display = getGiftItemDisplay(item);
        return (
          <Fragment key={item.id}>
            {(index > 0 || reward.reward_coupon) && ' + '}
            {Number(display.quantity) !== 1 && <>{display.quantity} × </>}
            {display.href ? (
              <Link
                href={ROUTES.PRODUCT({ slug: display.href })}
                prefetch={false}
                className="font-medium text-primary underline underline-offset-2 hover:no-underline"
              >
                {display.name || fallback}
              </Link>
            ) : (
              display.name || fallback
            )}
          </Fragment>
        );
      })}
      {!reward.items.length && !reward.reward_coupon && fallback}
    </>
  );
}

export function GiftRewardContents({ reward }: { reward: GiftReward }) {
  const locale = useLocaleStore(s => s.currentLocale);
  return (
    <div className="min-w-0 space-y-2" title={giftPackageLabel(locale)}>
      {reward.reward_coupon && (
        <div className="flex items-center gap-3">
          <GiftCouponValue coupon={reward.reward_coupon} className="h-11 w-11 shrink-0 text-sm" />
          <span className="min-w-0 break-words text-sm font-medium leading-5">
            {reward.reward_coupon.name}
          </span>
        </div>
      )}
      {reward.items.map(item => {
        const display = getGiftItemDisplay(item);
        const content = (
          <>
            <Image
              src={display.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(display.image, display.name)}
              width={44}
              height={44}
              sizes="44px"
              className="h-11 w-11 shrink-0 object-contain"
            />
            <span className="min-w-0 break-words text-sm font-medium leading-5">
              <span className="whitespace-nowrap">{display.quantity} × </span>
              {display.name || reward.name}
            </span>
          </>
        );
        return display.href ? (
          <Link
            key={item.id}
            href={ROUTES.PRODUCT({ slug: display.href })}
            prefetch={false}
            className="flex w-fit max-w-full items-center gap-3 underline-offset-2 hover:underline"
          >
            {content}
          </Link>
        ) : (
          <div key={item.id} className="flex w-fit max-w-full items-center gap-3">
            {content}
          </div>
        );
      })}
      {!reward.items.length && !reward.reward_coupon && (
        <span className="text-sm">{reward.name}</span>
      )}
    </div>
  );
}
